import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { GlassCard } from "@/components/common/GlassCard";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingState } from "@/components/common/States";
import { StatusPill } from "@/components/common/StatusPill";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Send, ChevronLeft, Mic, Trophy, AlertCircle, Sparkles, Flag, MessageSquare,
  CheckCircle2, RefreshCw, Pencil, Save, X,
} from "lucide-react";

const DIM_LABELS = {
  clarity: "Clarity",
  confidence: "Confidence",
  authenticity: "Authenticity",
  accountability: "Accountability",
  football_iq: "Football IQ",
  presence: "Presence",
};

const TYPE_LABEL = {
  combine: "Combine Interview",
  team_formal: "Team Formal Interview",
  informal_coach: "Informal Coach Conversation",
  position_specific: "Position-Specific",
  character: "Character / Background",
  injury_adversity: "Injury / Adversity",
};

function ScoreMeter({ value, label, testid }) {
  const v = Number(value || 0);
  const pct = Math.max(0, Math.min(100, (v / 10) * 100));
  return (
    <div className="flex flex-col gap-1" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-3 text-[10px] uppercase tracking-wider">{label}</span>
        <span className={`font-mono text-sm ${v >= 8 ? "text-emerald-300" : v >= 6 ? "text-cyan-200" : v >= 4 ? "text-amber-200" : "text-rose-200"}`}>{v}/10</span>
      </div>
      <div className="h-1.5 w-full rounded bg-white/5 overflow-hidden">
        <div className="h-full rounded transition-all" style={{ width: `${pct}%`, background: v >= 8 ? "#86efac" : v >= 6 ? "#7dd3fc" : v >= 4 ? "#fcd34d" : "#fda4af" }} />
      </div>
    </div>
  );
}

function FeedbackPanel({ turn, isAdmin, onFlag, onSaveOverride }) {
  const fb = turn.ai_feedback || {};
  const [editingOverride, setEditingOverride] = useState(false);
  const [overrideText, setOverrideText] = useState(turn.admin_override_md || "");
  const [busy, setBusy] = useState(false);

  return (
    <div className="space-y-3" data-testid={`ip-turn-feedback-${turn.id}`}>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {Object.entries(DIM_LABELS).map(([k, l]) => (
          <ScoreMeter key={k} value={(turn.scores || {})[k]} label={l} testid={`ip-turn-score-${turn.id}-${k}`} />
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {fb.strengths_md && (
          <div className="glass p-3" data-testid={`ip-turn-strengths-${turn.id}`}>
            <div className="text-[10px] uppercase tracking-wider text-emerald-200 flex items-center gap-1.5"><Trophy className="h-3 w-3" /> What was strong</div>
            <div className="markdown-body text-2 text-[13px] mt-1.5"><ReactMarkdown remarkPlugins={[remarkGfm]}>{fb.strengths_md}</ReactMarkdown></div>
          </div>
        )}
        {fb.concerns_md && (
          <div className="glass p-3" data-testid={`ip-turn-concerns-${turn.id}`}>
            <div className="text-[10px] uppercase tracking-wider text-rose-200 flex items-center gap-1.5"><AlertCircle className="h-3 w-3" /> What raised concern</div>
            <div className="markdown-body text-2 text-[13px] mt-1.5"><ReactMarkdown remarkPlugins={[remarkGfm]}>{fb.concerns_md}</ReactMarkdown></div>
          </div>
        )}
        {fb.team_perception_md && (
          <div className="glass p-3" data-testid={`ip-turn-perception-${turn.id}`}>
            <div className="text-[10px] uppercase tracking-wider text-cyan-200">What teams might think</div>
            <div className="markdown-body text-2 text-[13px] mt-1.5"><ReactMarkdown remarkPlugins={[remarkGfm]}>{fb.team_perception_md}</ReactMarkdown></div>
          </div>
        )}
        {fb.improved_answer_md && (
          <div className="glass p-3 ring-1 ring-violet-300/25" data-testid={`ip-turn-improved-${turn.id}`}>
            <div className="text-[10px] uppercase tracking-wider text-violet-200 flex items-center gap-1.5"><Sparkles className="h-3 w-3" /> Improved version</div>
            <div className="markdown-body text-2 text-[13px] mt-1.5 leading-relaxed"><ReactMarkdown remarkPlugins={[remarkGfm]}>{fb.improved_answer_md}</ReactMarkdown></div>
          </div>
        )}
      </div>
      {fb.follow_up_question && (
        <div className="glass p-3" data-testid={`ip-turn-followup-${turn.id}`}>
          <div className="text-[10px] uppercase tracking-wider text-amber-200 flex items-center gap-1.5"><MessageSquare className="h-3 w-3" /> Suggested follow-up</div>
          <div className="text-2 text-[13px] mt-1 italic">"{fb.follow_up_question}"</div>
        </div>
      )}

      {/* Admin override */}
      {isAdmin && (
        <div className="glass p-3 ring-1 ring-amber-300/30" data-testid={`ip-turn-admin-${turn.id}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-wider text-amber-200">Admin tools</div>
            <Button
              variant="ghost"
              size="sm"
              onClick={async () => {
                setBusy(true);
                try { await onFlag(turn, !turn.admin_flagged); } finally { setBusy(false); }
              }}
              disabled={busy}
              className={`text-3 hover:text-white ${turn.admin_flagged ? "ring-1 ring-rose-400/50 text-rose-200" : ""}`}
              data-testid={`ip-turn-flag-${turn.id}`}
            >
              <Flag className="h-3 w-3" /> {turn.admin_flagged ? "Flagged" : "Flag weak answer"}
            </Button>
          </div>
          {!editingOverride ? (
            <>
              {turn.admin_override_md ? (
                <div>
                  <div className="markdown-body text-2 text-[13px]"><ReactMarkdown remarkPlugins={[remarkGfm]}>{turn.admin_override_md}</ReactMarkdown></div>
                  <Button variant="ghost" size="sm" onClick={() => { setOverrideText(turn.admin_override_md || ""); setEditingOverride(true); }} className="text-3 hover:text-white mt-1">
                    <Pencil className="h-3 w-3" /> Edit override
                  </Button>
                </div>
              ) : (
                <Button variant="ghost" size="sm" onClick={() => setEditingOverride(true)} className="text-3 hover:text-white" data-testid={`ip-turn-override-add-${turn.id}`}>
                  <Pencil className="h-3 w-3" /> Add admin override / note
                </Button>
              )}
            </>
          ) : (
            <div className="space-y-2">
              <Textarea
                value={overrideText}
                onChange={(e) => setOverrideText(e.target.value)}
                placeholder="Override AI feedback or add advisor-only notes (markdown supported)…"
                className="bg-white/5 border-white/10 text-1 text-[13px] min-h-[100px]"
                data-testid={`ip-turn-override-textarea-${turn.id}`}
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  className="cta-primary"
                  data-testid={`ip-turn-override-save-${turn.id}`}
                  onClick={async () => {
                    setBusy(true);
                    try { await onSaveOverride(turn, overrideText); setEditingOverride(false); } finally { setBusy(false); }
                  }}
                  disabled={busy}
                >
                  <Save className="h-3 w-3" /> Save
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setEditingOverride(false)} className="text-3"><X className="h-3 w-3" /> Cancel</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CompletedSummary({ session }) {
  return (
    <GlassCard className="p-5" data-testid="ip-session-summary">
      <div className="flex items-center gap-3">
        <CheckCircle2 className="h-5 w-5 text-emerald-300" />
        <div>
          <div className="text-3 text-[10px] uppercase tracking-wider">Session complete</div>
          <div className="text-1 font-semibold">Overall score {session.overall_score}/10</div>
        </div>
        <div className="ml-auto text-3 text-[11px]">{session.completed_at ? new Date(session.completed_at).toLocaleString() : ""}</div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 mt-4">
        {Object.entries(DIM_LABELS).map(([k, l]) => (
          <ScoreMeter key={k} value={(session.avg_scores || {})[k]} label={l} testid={`ip-session-avg-${k}`} />
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
        <div className="glass p-3" data-testid="ip-session-strengths">
          <div className="text-[10px] uppercase tracking-wider text-emerald-200 flex items-center gap-1.5"><Trophy className="h-3 w-3" /> Biggest strengths</div>
          <ul className="text-2 text-[12.5px] mt-1.5 space-y-1 list-disc list-inside">
            {(session.biggest_strengths || []).map((s, i) => <li key={i}>{s}</li>)}
            {(session.biggest_strengths || []).length === 0 && <li className="text-3 italic">—</li>}
          </ul>
        </div>
        <div className="glass p-3" data-testid="ip-session-concerns">
          <div className="text-[10px] uppercase tracking-wider text-rose-200 flex items-center gap-1.5"><AlertCircle className="h-3 w-3" /> Biggest concerns</div>
          <ul className="text-2 text-[12.5px] mt-1.5 space-y-1 list-disc list-inside">
            {(session.biggest_concerns || []).map((s, i) => <li key={i}>{s}</li>)}
            {(session.biggest_concerns || []).length === 0 && <li className="text-3 italic">—</li>}
          </ul>
        </div>
        <div className="glass p-3 ring-1 ring-violet-300/25" data-testid="ip-session-improvements">
          <div className="text-[10px] uppercase tracking-wider text-violet-200 flex items-center gap-1.5"><Sparkles className="h-3 w-3" /> Top improvements</div>
          <ul className="text-2 text-[12.5px] mt-1.5 space-y-1 list-disc list-inside">
            {(session.top_improvements || []).map((s, i) => <li key={i}>{s}</li>)}
            {(session.top_improvements || []).length === 0 && <li className="text-3 italic">—</li>}
          </ul>
        </div>
      </div>
    </GlassCard>
  );
}

function AdvisorNote({ session, isAdmin, onSave }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(session.advisor_note || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setText(session.advisor_note || ""); }, [session.advisor_note]);

  return (
    <GlassCard className="p-4" data-testid="ip-advisor-note">
      <div className="flex items-center justify-between">
        <div className="text-3 text-[10px] uppercase tracking-wider">Advisor note</div>
        {isAdmin && !editing && (
          <Button variant="ghost" size="sm" onClick={() => setEditing(true)} className="text-3 hover:text-white" data-testid="ip-advisor-note-edit">
            <Pencil className="h-3 w-3" /> Edit
          </Button>
        )}
      </div>
      {!editing ? (
        session.advisor_note ? (
          <div className="markdown-body text-2 text-[13px] mt-1.5"><ReactMarkdown remarkPlugins={[remarkGfm]}>{session.advisor_note}</ReactMarkdown></div>
        ) : (
          <div className="text-3 text-[12px] mt-1.5 italic">{isAdmin ? "Add a note for this session…" : "Advisor has not added notes yet."}</div>
        )
      ) : (
        <div className="space-y-2 mt-2">
          <Textarea value={text} onChange={(e) => setText(e.target.value)} className="bg-white/5 border-white/10 text-1 min-h-[100px]" data-testid="ip-advisor-note-textarea" />
          <div className="flex gap-2">
            <Button size="sm" className="cta-primary" disabled={busy} data-testid="ip-advisor-note-save"
              onClick={async () => { setBusy(true); try { await onSave(text); setEditing(false); } finally { setBusy(false); } }}>
              <Save className="h-3 w-3" /> Save
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setText(session.advisor_note || ""); setEditing(false); }} className="text-3"><X className="h-3 w-3" /> Cancel</Button>
          </div>
        </div>
      )}
    </GlassCard>
  );
}

export default function InterviewSessionPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const isAdmin = user?.role === "admin";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [retryingQ, setRetryingQ] = useState(false);
  const liveAreaRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/interview/sessions/${sessionId}`);
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load session"); } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sessionId]);

  const session = data?.session;
  const turns = data?.turns || [];

  const currentTurn = useMemo(() => {
    if (!session) return null;
    if (session.status !== "active") return null;
    return turns.find((t) => t.turn_index === session.current_turn_index);
  }, [session, turns]);

  const answeredTurns = useMemo(() => turns.filter((t) => (t.answer_text || "").trim()), [turns]);
  const allAnswered = session && answeredTurns.length >= (session.target_turns || 0);

  const submitAnswer = async () => {
    if (!draft.trim()) { toast.error("Type an answer first"); return; }
    setSubmitting(true);
    try {
      const r = await api.post(`/interview/sessions/${sessionId}/answer`, { answer_text: draft });
      // Update state with answered turn + session + next turn (if any)
      setData((prev) => {
        if (!prev) return prev;
        const updatedTurns = prev.turns.map((t) => t.id === r.data.answered_turn.id ? r.data.answered_turn : t);
        if (r.data.next_turn && !updatedTurns.find((t) => t.id === r.data.next_turn.id)) updatedTurns.push(r.data.next_turn);
        return { session: r.data.session, turns: updatedTurns };
      });
      setDraft("");
      setTimeout(() => { liveAreaRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, 200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed");
    } finally { setSubmitting(false); }
  };

  const retryQuestion = async () => {
    setRetryingQ(true);
    try {
      const r = await api.post(`/interview/sessions/${sessionId}/next-question`);
      setData((prev) => {
        if (!prev) return prev;
        if (prev.turns.find((t) => t.id === r.data.id)) return prev;
        return { ...prev, turns: [...prev.turns, r.data] };
      });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to fetch next question"); } finally { setRetryingQ(false); }
  };

  const completeSession = async () => {
    setCompleting(true);
    try {
      const r = await api.post(`/interview/sessions/${sessionId}/complete`);
      setData((prev) => ({ ...prev, session: r.data }));
      toast.success("Session completed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Complete failed"); } finally { setCompleting(false); }
  };

  const flagTurn = async (turn, flagged) => {
    try {
      const r = await api.patch(`/interview/turns/${turn.id}`, { admin_flagged: flagged });
      setData((prev) => ({ ...prev, turns: prev.turns.map((t) => t.id === turn.id ? r.data : t) }));
      toast.success(flagged ? "Answer flagged" : "Flag removed");
    } catch { toast.error("Failed"); }
  };

  const saveOverride = async (turn, text) => {
    try {
      const r = await api.patch(`/interview/turns/${turn.id}`, { admin_override_md: text });
      setData((prev) => ({ ...prev, turns: prev.turns.map((t) => t.id === turn.id ? r.data : t) }));
      toast.success("Override saved");
    } catch { toast.error("Failed"); }
  };

  const saveAdvisorNote = async (text) => {
    try {
      const r = await api.patch(`/interview/sessions/${sessionId}`, { advisor_note: text });
      setData((prev) => ({ ...prev, session: r.data }));
      toast.success("Note saved");
    } catch { toast.error("Failed"); }
  };

  if (loading) return <LoadingState />;
  if (!session) return <div className="text-3">Session not found.</div>;

  return (
    <div className="space-y-5" data-testid="ip-session-page">
      <PageHeader
        title={`Interview · ${(session.difficulty || "").replace(/_/g, " ")}`}
        subtitle={`${TYPE_LABEL[session.type] || session.type}${session.status === "active" ? " · AI evaluator in progress" : " · Review evaluator scores and feedback"}`}
        actions={(
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate(-1)} className="text-2 hover:text-white" data-testid="ip-session-back">
              <ChevronLeft className="h-4 w-4" /> Back
            </Button>
            {session.status === "active" && answeredTurns.length > 0 && (
              <Button onClick={completeSession} disabled={completing} className="cta-primary" data-testid="ip-session-complete">
                <CheckCircle2 className="h-4 w-4" /> {completing ? "Completing…" : (allAnswered ? "Complete session" : "End early & summarise")}
              </Button>
            )}
          </div>
        )}
      />

      {session.status === "completed" && <CompletedSummary session={session} />}

      <AdvisorNote session={session} isAdmin={isAdmin} onSave={saveAdvisorNote} />

      <GlassCard className="p-4" data-testid="ip-progress">
        <div className="flex items-center gap-2">
          <StatusPill tone={session.status === "active" ? "cyan" : session.status === "completed" ? "violet" : "neutral"}>{session.status}</StatusPill>
          <span className="text-3 text-[11px]">Turn {Math.min(session.current_turn_index + 1, session.target_turns)} of {session.target_turns}</span>
        </div>
        <div className="mt-2 h-1 w-full rounded bg-white/5 overflow-hidden">
          <div className="h-full bg-cyan-300/60 transition-all" style={{ width: `${Math.min(100, (answeredTurns.length / (session.target_turns || 1)) * 100)}%` }} />
        </div>
      </GlassCard>

      <div className="space-y-5">
        {turns.map((turn, idx) => {
          const isCurrent = session.status === "active" && turn.id === currentTurn?.id;
          const isAnswered = !!(turn.answer_text || "").trim();
          return (
            <GlassCard key={turn.id} className="p-5 relative overflow-hidden" data-testid={`ip-turn-${turn.id}`}>
              <div className="absolute -top-12 -right-12 h-32 w-32 rounded-full pointer-events-none opacity-50" style={{ background: "radial-gradient(closest-side, rgba(125,211,252,0.16), transparent 70%)" }} />
              <div className="relative">
                <div className="flex items-center gap-2 mb-2">
                  <StatusPill tone="cyan">Q{idx + 1}</StatusPill>
                  {turn.question_category && <StatusPill tone="neutral">{turn.question_category}</StatusPill>}
                  {turn.admin_flagged && <StatusPill tone="rose">Admin flagged</StatusPill>}
                  {isAnswered && (
                    <span className="ml-auto text-2 text-[12px] font-mono">Overall {turn.overall_score}/10</span>
                  )}
                </div>
                <div className="text-1 text-[15.5px] leading-relaxed font-semibold" data-testid={`ip-turn-question-${turn.id}`}>{turn.question_text}</div>

                {isAnswered && (
                  <>
                    <div className="glass p-3 mt-3" data-testid={`ip-turn-answer-${turn.id}`}>
                      <div className="text-[10px] uppercase tracking-wider text-3">Your answer</div>
                      <div className="text-2 text-[13px] mt-1 whitespace-pre-wrap leading-relaxed">{turn.answer_text}</div>
                    </div>
                    <div className="mt-4">
                      <FeedbackPanel turn={turn} isAdmin={isAdmin} onFlag={flagTurn} onSaveOverride={saveOverride} />
                    </div>
                  </>
                )}

                {isCurrent && !isAnswered && (
                  <div className="mt-4 space-y-3" ref={liveAreaRef}>
                    <Label className="text-3 text-[11px] uppercase tracking-wider">Your answer</Label>
                    <Textarea
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      placeholder="Speak as if the evaluator is across the table from you. Be specific — name games, plays, people, outcomes."
                      className="bg-white/5 border-white/10 text-1 min-h-[160px] text-[14px] leading-relaxed"
                      data-testid="ip-answer-textarea"
                    />
                    <div className="flex items-center gap-2">
                      <Button onClick={submitAnswer} disabled={submitting || !draft.trim()} className="cta-primary" data-testid="ip-answer-submit">
                        <Send className="h-4 w-4" /> {submitting ? "Evaluating…" : "Submit answer"}
                      </Button>
                      <span className="text-3 text-[11px]">AI will score on 6 dimensions and give you feedback before the next question.</span>
                    </div>
                  </div>
                )}
              </div>
            </GlassCard>
          );
        })}

        {/* Active session but next question failed to generate */}
        {session.status === "active" && !currentTurn && session.current_turn_index < session.target_turns && (
          <GlassCard className="p-4 ring-1 ring-amber-300/30" data-testid="ip-next-q-retry">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3 text-[10px] uppercase tracking-wider text-amber-200">Next question not generated</div>
                <div className="text-2 text-[13px] mt-1">The AI evaluator hasn't produced the next question yet.</div>
              </div>
              <Button onClick={retryQuestion} disabled={retryingQ} className="cta-primary">
                <RefreshCw className="h-4 w-4" /> {retryingQ ? "Generating…" : "Generate next"}
              </Button>
            </div>
          </GlassCard>
        )}
      </div>
    </div>
  );
}
